import type { GbpSuggestionSource, ProfileFindings } from "../../../../services/visibilityService";
import type {
  GbpAttributeOption,
  GbpAttributesResponse,
  GbpAuditLocation,
  GbpAuditResponse,
  GbpCategoryOption,
  GbpDay,
  GbpEditField,
  GbpEditResult,
  GbpEditValue,
  GbpGap,
  GbpHoursPeriod,
  GbpOpeningDate,
  GbpPhotoCategory,
  GbpPhotoResult,
  GbpServiceEdit,
} from "../../../../services/gbpService";
import type { GbpPlan } from "./gbpPlan";
import { GBP_PHOTO_CATEGORIES } from "../../../../services/gbpConstants";
import type { MediaItem } from "../../../../types/MediaItem";
import { getMediaThumbnailUrl, isImageMediaItem } from "../../../../types/MediaItem";
import type { CleanFinding, FindingGroup } from "../../../../features/analytics/profileFindingsModel";
import {
  cleanFinding,
  groupFindings,
  isRecord,
  plural,
  serviceKey,
} from "../../../../features/analytics/profileFindingsModel";

/**
 * The Visibility Manager's Business Profile card, as data (SGL-328 Phase B/C).
 * Pure: no React, no fetching (gbpService is imported for types only).
 *
 * Each fix moves through: suggested (from a real source) → edited by the owner
 * (optional) → approved (ticked) → applied (one edit, one call) → what Google did.
 */

/* ── what the card carries ─────────────────────────────────────────────── */

/** An agent the owner already has, named the way they named it. */
export interface GbpTeamMember {
  id: string;
  name: string;
}

/** Who a hand-off row points at (review replies → Receptionist, posts → Social
 *  Media Manager). null = the owner has no such agent. */
export interface GbpProposalTeam {
  receptionist: GbpTeamMember | null;
  socialMediaManager: GbpTeamMember | null;
}

/** The profile audit, cut down to what the card needs. Only "ok" has gaps: a
 *  profile we could not read is never shown as one with nothing missing. */
export type GbpAuditState =
  | {
      status: "ok";
      locationId: string;
      gaps: GbpGap[];
      /** The main category on Google now; absent on a card stored before it, or
       *  null when the business-info section did not load. */
      primaryCategory?: GbpCategoryOption | null;
    }
  | { status: "not_connected" | "needs_reauth" | "failed" };

/** What Google offers this location that Sagelyn can set (GET /gbp/attributes).
 *  "failed" = we could not read it; nothing is offered then. */
export type GbpAttributesState =
  | { status: "ok"; items: GbpAttributeOption[]; notEditableCount: number }
  | { status: "failed" };

/** The Media Library image the owner picked for the profile, and what it shows.
 *  `category` stays null until the owner chooses: there is no default. */
export interface PhotoPick {
  sourceUrl: string;
  category: GbpPhotoCategory | null;
}

/** A value on the card: text, a list of names, Google categories, a week of
 *  opening hours, or an opening date. */
export type FixValue = string | string[] | GbpCategoryOption[] | GbpHoursPeriod[] | GbpOpeningDate;

/** Who proposed the value the row shows. "owner" = typed or edited by the owner.
 *  "typical" = common opening hours shown as a starting point: a reference, not
 *  the owner's data, so it is never sent until the owner saves it as theirs. */
export type ValueSource = GbpSuggestionSource | "lost_searches" | "owner" | "typical";

export interface StoredSuggestion {
  value: unknown;
  source: GbpSuggestionSource;
}

/**
 * One edit's outcome, kept on the card as its activity log (AC8).
 *   applied_unverified — Google took the write, the read-back failed: sent, not
 *                        confirmed (the next check looks again).
 *   submitted          — a photo Google accepted; Google gives no review state.
 *   no_answer          — the request went out but no usable answer came back
 *                        (network error, timeout, server error): it may or may
 *                        not be on Google. Never "failed" (that says nothing
 *                        changed), never settled (the owner may check and retry).
 */
/** What to re-read on Google to know where an applied change stands now. */
export type StatusCheck =
  | { field: GbpEditField; value: GbpEditValue }
  | { field: "photo"; mediaName: string };

export interface EditOutcome {
  /** not_on_google: a later check found Google shows something else and nothing
   *  is pending (Google changed it back or never took it). */
  status:
    | "applying" | "live" | "pending_review" | "applied_unverified" | "submitted" | "no_answer" | "not_on_google" | "failed";
  old?: GbpEditValue | null;
  now?: GbpEditValue | null;
  message?: string;
  /** Something the owner should know even though the edit went through. */
  warning?: string;
  /** ISO time the result came back. */
  at?: string;
  /** How to re-check it on Google. Absent on cards stored before live status. */
  check?: StatusCheck;
  /** ISO time of the last re-check. */
  checkedAt?: string;
}

/** The Business Profile proposal attached to a chat reply (Message.gbpProposal). */
export interface GbpProposalAttachment {
  /** The last check is still being read. */
  loading: boolean;
  /** The owner typed the request, so the agent's reply above already says it is fetching. */
  askedInChat?: boolean;
  /** Reading the last check failed (network or service error). */
  failed: boolean;
  /** The check's findings block; null when it has none (older run, or never checked). */
  findings: ProfileFindings | null;
  /** The live profile audit. Absent on a card stored before it existed. */
  audit?: GbpAuditState | null;
  team?: GbpProposalTeam;
  /** Suggested values by gap code, each from a real source. */
  suggestions?: Record<string, StoredSuggestion>;
  /** Suggestions are still being worked out. */
  suggestionsLoading?: boolean;
  /** Values the owner typed or edited, by gap code. These win over suggestions. */
  answers?: Record<string, FixValue>;
  /** What happened to each applied row, by row key. */
  applied?: Record<string, EditOutcome>;
  /** AC11: the owner confirmed Google's own automatic updates are off. */
  googleAutoOff?: boolean;
  /** SGL-421: an approval was stopped because Google had changed these fields
   *  on its own (its automatic updates are still on). Cleared when the owner
   *  confirms again. [] = Google changed something it didn't name. */
  googleUpdatedNow?: string[];
  /** SGL-421: asked "still off?" about an old confirmation, the owner said
   *  they're back on, so the card shows the full settings steps again. */
  googleBackOn?: boolean;
  /** Attributes Google offers this location. Absent = not read (older card, or no audit). */
  attributes?: GbpAttributesState | null;
  /** The value the owner chose per attribute id. Never preselected. */
  attributeAnswers?: Record<string, boolean | string>;
  /** The main category the owner picked. Never preselected. */
  primaryAnswer?: GbpCategoryOption | null;
  /** The Media Library photo the owner picked. */
  photo?: PhotoPick | null;
  /** The owner pressed "Not now". */
  dismissed?: boolean;
  /** SGL-421: the full proposal the Visibility Manager prepared (utils/gbpPlan.ts).
   *  Present = the v2 card; absent = a v1 card, rendered as it always was. */
  plan?: GbpPlan | null;
  /** Building the plan failed; the card says so instead of showing nothing. */
  planError?: string | null;
  /** v2: values the owner edited or typed, by plan row id. These win over the plan's. */
  planAnswers?: Record<string, GbpEditValue>;
  /** v2: rows the owner skipped, by plan row id. */
  skipped?: Record<string, boolean>;
  /** v2: manual rows the owner marked done on Google, by plan row id. */
  doneOnGoogle?: Record<string, boolean>;
  /** v2: rows the Visibility Manager changed for the owner from chat (revise_gbp_row), by plan row id. */
  revisedInChat?: Record<string, boolean>;
  /** Where the card is stored (POST /visibility/gbp-proposal); later changes rewrite it. */
  threadId?: string | null;
  cardMessageSk?: string | null;
}

/* ── the gap catalogue ─────────────────────────────────────────────────── */

export type FixKind = "text" | "multiline" | "list" | "categories" | "hours" | "date";

export interface FixSpec {
  title: string;
  kind: FixKind;
  placeholder: string;
  maxLength: number;
  /** The Google field an approval writes. Absent = Sagelyn can't set it yet. */
  field?: GbpEditField;
}

/** Empty profile fields. Suggestions come only from real sources (Brand
 *  details, Google's categories, a description written from Brand details).
 *  Hours and opening date have no source: the owner sets them (hours start from
 *  TYPICAL_HOURS as a reference the owner must confirm). */
export const ASK_GAPS: Record<string, FixSpec> = {
  no_service_items: { title: "List your services", kind: "list", placeholder: "Drain cleaning, Water heater repair", maxLength: 300, field: "services" },
  no_additional_categories: { title: "Add categories", kind: "categories", placeholder: "Search Google's categories", maxLength: 80, field: "additional_categories" },
  // Google's limit for profile.description.
  empty_description: { title: "Add a description", kind: "multiline", placeholder: "What you do, for whom, and where", maxLength: 750, field: "description" },
  no_website: { title: "Add your website", kind: "text", placeholder: "https://…", maxLength: 200, field: "website" },
  no_phone: { title: "Add a phone number", kind: "text", placeholder: "(512) 555-0100", maxLength: 40, field: "phone" },
  no_service_area: { title: "Set service area", kind: "list", placeholder: "Georgetown, Round Rock", maxLength: 300 },
  no_regular_hours: { title: "Set opening hours", kind: "hours", placeholder: "", maxLength: 0, field: "regular_hours" },
  no_opening_date: { title: "Add opening date", kind: "date", placeholder: "", maxLength: 0, field: "opening_date" },
};

/** Common opening hours, shown as a starting point the owner edits and
 *  confirms. Never sent as they are (see ValueSource "typical"). */
export const TYPICAL_HOURS: GbpHoursPeriod[] = [
  ...(["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"] as const).map((day) => ({ day, open: "09:00", close: "18:00" })),
  { day: "SATURDAY", open: "10:00", close: "16:00" },
];

export type TeamRole = keyof GbpProposalTeam;

export interface HandOffSpec {
  title: string;
  role: TeamRole;
  /** The role as a job title, for when the owner has no such agent. */
  roleLabel: string;
  /** Pre-filled in that agent's chat; the owner sends it. */
  prompt: string;
  /** One line on why this matters, shown on the team card. */
  why: string;
  /** What that team member does about it, e.g. "write posts for you". */
  does: string;
}

/** Work that belongs to another agent. Hand-offs name the agent the way the
 *  owner named it and open that agent's chat; nothing is passed silently. */
export const HANDOFF_GAPS: Record<string, HandOffSpec> = {
  reviews_without_reply: {
    title: "Reply to reviews",
    role: "receptionist",
    roleLabel: "Receptionist",
    prompt: "Draft replies to my Google reviews that don't have a reply yet.",
    why: "Replying to reviews shows Google and customers that you care.",
    does: "draft the replies for you",
  },
  no_posts: {
    title: "Post on Google",
    role: "socialMediaManager",
    roleLabel: "Social Media Manager",
    prompt: "Write a Google Business Profile post for my business this week.",
    why: "Regular posts show Google your business is active, so you show up in more searches.",
    does: "write them for you",
  },
  stale_posts: {
    title: "Post on Google",
    role: "socialMediaManager",
    roleLabel: "Social Media Manager",
    prompt: "Write a Google Business Profile post for my business this week.",
    why: "Regular posts show Google your business is active, so you show up in more searches.",
    does: "write them for you",
  },
};

/** What the team card says under its title: why the work matters and what "Ask" does. */
export function handOffExplain(rows: HandOffRow[]): string {
  if (rows.length !== 1) return "Your other AI team members can take care of these for you.";
  const [{ spec, member }] = rows;
  return member
    ? `${spec.why} Your ${spec.roleLabel} can ${spec.does}.`
    : `${spec.why} Add a ${spec.roleLabel} to your team and they can ${spec.does}.`;
}

/** Gaps nobody in the product can close yet: stated, nothing offered. */
const NOTE_TITLES: Record<string, string> = {
  no_reviews: "No reviews yet",
  no_photos: "No photos yet",
};

/** The gap the photo card answers. It is the only photo signal the audit gives
 *  ("no photos"); there is no "too few" threshold, so none is invented here. */
const PHOTO_GAP = "no_photos";

/** Q&A: Google shut the API down (2025-11-03). The backend never sends these;
 *  if it ever did, the card must not ask for work nobody can do. */
const IGNORED_GAPS = new Set(["no_questions", "unanswered_questions"]);

export const SOURCE_LABELS: Record<ValueSource, string> = {
  brand_details: "From your Brand details",
  drafted: "Written from your Brand details",
  google_categories: "From Google's category list",
  lost_searches: "From your lost searches",
  owner: "Your edit",
  typical: "Typical hours. Set your own before adding.",
};

/* ── values ────────────────────────────────────────────────────────────── */

const isCategory = (v: unknown): v is GbpCategoryOption =>
  isRecord(v) && typeof v.name === "string" && typeof v.display_name === "string";

/** Google's week order (gbp_edits.DAYS). */
export const GBP_DAYS: readonly GbpDay[] = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

const CLOCK = /^(?:[01]\d|2[0-3]):[0-5]\d$|^24:00$/;
const isDay = (v: unknown): v is GbpDay => typeof v === "string" && (GBP_DAYS as readonly string[]).includes(v);
const isPeriod = (v: unknown): v is GbpHoursPeriod =>
  isRecord(v) && isDay(v.day) && typeof v.open === "string" && CLOCK.test(v.open) && typeof v.close === "string" && CLOCK.test(v.close);
const isWholeNumber = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v);
export const isOpeningDate = (v: unknown): v is GbpOpeningDate =>
  isRecord(v) && isWholeNumber(v.year) && isWholeNumber(v.month) && (v.day === undefined || isWholeNumber(v.day));
export const isHours = (v: unknown): v is GbpHoursPeriod[] => Array.isArray(v) && v.length > 0 && v.every(isPeriod);

/** `raw` as a value of `kind`, or null when it isn't one (or is empty). */
export function asFixValue(kind: FixKind, raw: unknown): FixValue | null {
  if (kind === "hours") return isHours(raw) ? raw : null;
  if (kind === "date") {
    return isOpeningDate(raw) ? { year: raw.year, month: raw.month, ...(raw.day ? { day: raw.day } : {}) } : null;
  }
  if (kind === "categories") {
    const cats = Array.isArray(raw) ? raw.filter(isCategory) : [];
    return cats.length ? cats : null;
  }
  if (kind === "list") {
    const list = Array.isArray(raw)
      ? raw.filter((x): x is string => typeof x === "string").map((x) => x.trim()).filter(Boolean)
      : [];
    return list.length ? list : null;
  }
  const s = typeof raw === "string" ? raw.trim() : "";
  return s ? s : null;
}

const DAY_SHORT: Record<GbpDay, string> = {
  MONDAY: "Mon", TUESDAY: "Tue", WEDNESDAY: "Wed", THURSDAY: "Thu", FRIDAY: "Fri", SATURDAY: "Sat", SUNDAY: "Sun",
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "13:30" -> "1:30 PM". Locale-free, so the card reads the same everywhere. */
export function formatClock(clock: string): string {
  const [h, m] = clock.split(":").map(Number);
  const hour = h % 24;
  return `${hour % 12 || 12}:${String(m).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}

/** A week as rows, runs of days with the same hours together:
 *  [{days: "Mon–Fri", hours: "9:00 AM – 6:00 PM"}, …, {days: "Sun", hours: "Closed"}]. */
export function hoursRows(periods: GbpHoursPeriod[]): Array<{ days: string; hours: string }> {
  const byDay = GBP_DAYS.map((day) => {
    const own = periods.filter((p) => p.day === day);
    return own.length ? own.map((p) => `${formatClock(p.open)} – ${formatClock(p.close)}`).join(", ") : "Closed";
  });
  const rows: Array<{ days: string; hours: string }> = [];
  for (let i = 0; i < GBP_DAYS.length; ) {
    let j = i;
    while (j + 1 < GBP_DAYS.length && byDay[j + 1] === byDay[i]) j++;
    rows.push({ days: i === j ? DAY_SHORT[GBP_DAYS[i]] : `${DAY_SHORT[GBP_DAYS[i]]}–${DAY_SHORT[GBP_DAYS[j]]}`, hours: byDay[i] });
    i = j + 1;
  }
  return rows;
}

/** "May 2019", or "12 May 2019" with a day. */
export const formatOpeningDate = (d: GbpOpeningDate): string =>
  `${d.day ? `${d.day} ` : ""}${MONTHS[d.month - 1] ?? d.month} ${d.year}`;

/** "a, b" for lists, display names for categories, a week or a date in words,
 *  the text itself otherwise. */
export function formatValue(v: FixValue | GbpEditValue | null | undefined): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (isOpeningDate(v)) return formatOpeningDate(v);
  if (isHours(v)) return hoursRows(v).map((r) => `${r.days} ${r.hours}`).join(" · ");
  if (!Array.isArray(v)) return "";
  return (v as Array<string | GbpCategoryOption | GbpServiceEdit>)
    .map((x) => (typeof x === "string" ? x : "display_name" in x ? x.display_name : x.name))
    .join(", ");
}

/** A comma-separated list typed by the owner. */
export const parseList = (text: string): string[] =>
  Array.from(new Set(text.split(",").map((s) => s.trim()).filter(Boolean)));

/** The value as the edit API takes it: category names, not display names. */
export function editValue(v: FixValue): GbpEditValue {
  if (typeof v === "string" || isOpeningDate(v) || isHours(v)) return v;
  return (v as Array<string | GbpCategoryOption>).map((x) => (typeof x === "string" ? x : x.name));
}

/* ── reading it ────────────────────────────────────────────────────────── */

export const serviceRowKey = (service: string) => `svc:${serviceKey(service)}`;
export const askRowKey = (code: string) => `ask:${code}`;
export const attributeRowKey = (id: string) => `attr:${id}`;
export const PRIMARY_ROW_KEY = "primary";
export const PHOTO_ROW_KEY = "photo";

export interface ServiceRow {
  key: string;
  group: FindingGroup;
  outcome: EditOutcome | null;
}

export interface AskRow {
  key: string;
  code: string;
  spec: FixSpec;
  detail: string;
  /** What the row proposes now: the owner's edit, else the suggestion, else null. */
  value: FixValue | null;
  source: ValueSource | null;
  outcome: EditOutcome | null;
  /** Sagelyn can write this field (spec.field is set). */
  appliable: boolean;
}

export interface HandOffRow {
  code: string;
  spec: HandOffSpec;
  detail: string;
  member: GbpTeamMember | null;
}

export interface NoteRow {
  code: string;
  title: string;
  detail: string;
}

/** The main category, offered as an editable row, never as a problem. */
export interface PrimaryRow {
  key: string;
  current: GbpCategoryOption;
  value: GbpCategoryOption | null;
  outcome: EditOutcome | null;
}

/** One attribute Google offers that has no value on the profile. */
export interface AttributeRow {
  key: string;
  option: GbpAttributeOption;
  /** The owner's choice; null until they pick. */
  value: boolean | string | null;
  outcome: EditOutcome | null;
}

export interface PhotoRow {
  key: string;
  detail: string;
  value: PhotoPick | null;
  outcome: EditOutcome | null;
}

export interface ProposalSections {
  services: ServiceRow[];
  /** Local searches lost, the card's one big number. */
  lost: number;
  ask: AskRow[];
  handOffs: HandOffRow[];
  notes: NoteRow[];
  /** Attributes with no value on Google (plus any already sent from this card). */
  attributes: AttributeRow[];
  /** Attributes Google offers that only the owner can set, on Google. */
  attributesNotEditable: number;
  photo: PhotoRow | null;
  /** Offered only when the card has something else to show. */
  primary: PrimaryRow | null;
}

/** One gap per missing service, plus how many local searches were lost. */
export function readProposal(block: ProfileFindings | null | undefined): { groups: FindingGroup[]; lost: number } {
  if (!isRecord(block) || block.status !== "ok" || !Array.isArray(block.findings)) {
    return { groups: [], lost: 0 };
  }
  const findings = block.findings.map(cleanFinding).filter((f): f is CleanFinding => f !== null);
  const lost = typeof block.questions_checked === "number" ? block.questions_checked : 0;
  return { groups: groupFindings(findings), lost };
}

const auditGaps = (p: GbpProposalAttachment): GbpGap[] =>
  p.audit && p.audit.status === "ok" && Array.isArray(p.audit.gaps)
    ? p.audit.gaps.filter((g) => isRecord(g) && typeof g.code === "string")
    : [];

export function readSections(p: GbpProposalAttachment): ProposalSections {
  const { groups, lost } = readProposal(p.findings);
  const answers = p.answers ?? {};
  const suggestions = p.suggestions ?? {};
  const applied = p.applied ?? {};
  const team = p.team ?? { receptionist: null, socialMediaManager: null };
  const ask: AskRow[] = [];
  const handOffs: HandOffRow[] = [];
  const notes: NoteRow[] = [];
  const seenHandOff = new Set<string>();

  let photo: PhotoRow | null = null;
  for (const g of auditGaps(p)) {
    const detail = typeof g.detail === "string" ? g.detail : "";
    if (IGNORED_GAPS.has(g.code)) continue;
    if (g.code === PHOTO_GAP) {
      photo = { key: PHOTO_ROW_KEY, detail, value: isPhotoPick(p.photo) ? p.photo : null, outcome: applied[PHOTO_ROW_KEY] ?? null };
      continue;
    }
    // The services the owner lost searches for are already the top section.
    if (g.code === "no_service_items" && groups.length > 0) continue;
    const spec = ASK_GAPS[g.code];
    if (spec) {
      const own = asFixValue(spec.kind, answers[g.code]);
      const suggested = own ? null : asFixValue(spec.kind, suggestions[g.code]?.value);
      // Hours have no source: show typical ones as a starting point to edit.
      const typical = !own && !suggested && spec.kind === "hours" ? TYPICAL_HOURS : null;
      const key = askRowKey(g.code);
      ask.push({
        key,
        code: g.code,
        spec,
        detail,
        value: own ?? suggested ?? typical,
        source: own ? "owner" : suggested ? suggestions[g.code].source : typical ? "typical" : null,
        outcome: applied[key] ?? null,
        appliable: !!spec.field,
      });
      continue;
    }
    const handOff = HANDOFF_GAPS[g.code];
    if (handOff) {
      // no_posts and stale_posts are the same job: one row.
      const key = `${handOff.role}:${handOff.title}`;
      if (seenHandOff.has(key)) continue;
      seenHandOff.add(key);
      handOffs.push({ code: g.code, spec: handOff, detail, member: team[handOff.role] ?? null });
      continue;
    }
    // A code this UI does not know yet: say what the backend said, offer nothing.
    notes.push({ code: g.code, title: NOTE_TITLES[g.code] ?? "Profile gap", detail });
  }
  const order = Object.keys(ASK_GAPS);
  // What Sagelyn can apply first, then what the owner sets on Google themselves.
  ask.sort((a, b) => Number(b.appliable) - Number(a.appliable) || order.indexOf(a.code) - order.indexOf(b.code));
  const services = groups.map((group) => {
    const key = serviceRowKey(group.service);
    return { key, group, outcome: applied[key] ?? null };
  });

  // Attributes: offered only where Google has no value yet. One the owner sent
  // from this card stays, so its result stays visible.
  const attrState = p.audit?.status === "ok" ? p.attributes : null;
  const attributes: AttributeRow[] = [];
  let attributesNotEditable = 0;
  if (attrState && attrState.status === "ok") {
    attributesNotEditable = attrState.notEditableCount;
    const chosen = p.attributeAnswers ?? {};
    for (const option of attrState.items) {
      const key = attributeRowKey(option.id);
      if (option.current != null && !applied[key]) continue;
      const pick = chosen[option.id];
      attributes.push({ key, option, value: isAttributeChoice(option, pick) ? pick : null, outcome: applied[key] ?? null });
    }
  }

  // The main category is never claimed to be wrong: no finding says so yet. It
  // is offered as an editable row only on a card that already has something to
  // show, so "what's holding my profile back?" is never answered with it alone.
  const current = p.audit?.status === "ok" ? p.audit.primaryCategory : null;
  const others = services.length + ask.length + handOffs.length + notes.length + attributes.length + (photo ? 1 : 0);
  const primary: PrimaryRow | null =
    current && isCategory(current) && (others > 0 || !!applied[PRIMARY_ROW_KEY])
      ? {
          key: PRIMARY_ROW_KEY,
          current,
          value: isCategory(p.primaryAnswer) ? p.primaryAnswer : null,
          outcome: applied[PRIMARY_ROW_KEY] ?? null,
        }
      : null;
  return { services, lost, ask, handOffs, notes, attributes, attributesNotEditable, photo, primary };
}

export const hasAnything = (s: ProposalSections): boolean =>
  s.services.length + s.ask.length + s.handOffs.length + s.notes.length + s.attributes.length + (s.photo ? 1 : 0) > 0;

/** Done = Google has it (live), is reviewing it, took it without confirming,
 *  or accepted the photo. Not offered again. */
export const isSettled = (o: EditOutcome | null | undefined) =>
  !!o &&
  (o.status === "live" || o.status === "pending_review" || o.status === "applied_unverified" || o.status === "submitted");

/** Sent, but not confirmed live yet: worth checking again. */
export const isWaiting = (o: EditOutcome | null | undefined) =>
  !!o &&
  (o.status === "pending_review" || o.status === "applied_unverified" || o.status === "submitted" || o.status === "no_answer");

/** Offered again: Google refused it, gave no answer, or no longer shows it. */
export const canRetry = (o: EditOutcome | null | undefined) =>
  o?.status === "failed" || o?.status === "no_answer" || o?.status === "not_on_google";

/** Where the card's sent changes stand: how many, and how many are live. */
export function liveProgress(applied: Record<string, EditOutcome> | undefined) {
  // No answer counts too: it may well be on Google, and the next check says.
  const tracked = Object.values(applied ?? {}).filter(
    (o) => isSettled(o) || o.status === "not_on_google" || o.status === "no_answer",
  );
  return {
    total: tracked.length,
    live: tracked.filter((o) => o.status === "live").length,
    waiting: tracked.filter(isWaiting).length,
    missing: tracked.filter((o) => o.status === "not_on_google").length,
    noAnswer: tracked.filter((o) => o.status === "no_answer").length,
  };
}

export type LiveState = "live" | "waiting" | "not_on_google" | "unknown";

/** One row of POST /gbp/edit-status. */
export type StatusCheckRequest =
  | { key: string; field: GbpEditField; value: GbpEditValue }
  | { key: string; field: "photo"; media_name: string };

/** The rows to re-check: everything still waiting, plus live rows when `all`
 *  (a live change can still be changed back by Google). Rows stored before
 *  live status have no check and keep their last word. */
export function statusChecks(applied: Record<string, EditOutcome> | undefined, all = false): StatusCheckRequest[] {
  const out: StatusCheckRequest[] = [];
  for (const [key, o] of Object.entries(applied ?? {})) {
    if (!o.check || !(isWaiting(o) || (all && o.status === "live"))) continue;
    const c = o.check as unknown;
    if (!isRecord(c)) continue;
    if (c.field === "photo") {
      if (typeof c.mediaName === "string" && c.mediaName) out.push({ key, field: "photo", media_name: c.mediaName });
    } else if (typeof c.field === "string" && c.value !== undefined && c.value !== null) {
      out.push({ key, field: c.field as GbpEditField, value: c.value as GbpEditValue });
    }
  }
  return out;
}

/** Fold a re-check into the outcomes. "unknown" (Google couldn't be read)
 *  changes nothing: the last known word stands. */
export function withLiveStates(
  applied: Record<string, EditOutcome> | undefined,
  results: Record<string, LiveState>,
  at: Date = new Date(),
): Record<string, EditOutcome> {
  const next: Record<string, EditOutcome> = { ...(applied ?? {}) };
  const checkedAt = at.toISOString();
  for (const [key, state] of Object.entries(results)) {
    const o = next[key];
    // An edit sent again since the check began is not this check's to judge.
    if (!o || o.status === "applying" || o.status === "failed") continue;
    const status: EditOutcome["status"] | null =
      state === "live" ? "live" : state === "waiting" ? (o.status === "submitted" ? "submitted" : "pending_review") : state === "not_on_google" ? "not_on_google" : null;
    next[key] = status ? { ...o, status, message: undefined, checkedAt } : { ...o, checkedAt };
  }
  return next;
}

const isPhotoCategory = (v: unknown): v is GbpPhotoCategory =>
  typeof v === "string" && GBP_PHOTO_CATEGORIES.some((c) => c.value === v);

const isPhotoPick = (v: unknown): v is PhotoPick =>
  isRecord(v) && typeof v.sourceUrl === "string" && /^https:\/\//i.test(v.sourceUrl) && (v.category === null || isPhotoCategory(v.category));

/** A value the owner may pick for this attribute: yes/no for BOOL, one of
 *  Google's own values for ENUM. */
export function isAttributeChoice(option: GbpAttributeOption, v: unknown): v is boolean | string {
  if (option.value_type === "BOOL") return typeof v === "boolean";
  return typeof v === "string" && option.options.some((o) => o.value === v);
}

/**
 * True when `url` is an https address whose path names the customer as one
 * whole segment, which social-management requires of a photo (validate_photo,
 * _owned_by). Which hosts it accepts is server config, so the server still
 * decides; this only keeps other tenants' and non-library images out of the
 * picker.
 */
export function isOwnMediaUrl(url: string, customerId: string): boolean {
  if (!customerId) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:" || parsed.username || parsed.password) return false;
  return parsed.pathname.split("/").some((seg) => {
    try {
      return decodeURIComponent(seg) === customerId;
    } catch {
      return false;
    }
  });
}

/** One "page" of the photo picker: the owner's images that may go on their profile. */
export interface GbpPhotoPage {
  items: MediaItem[];
  /** Pass back to load the next page; null when the library has no more. */
  nextToken: string | null;
}

/** The media API's largest page (content-management upload_media MAX_PAGE_SIZE). */
export const GBP_PHOTO_PAGE_SIZE = 50;
/** Requests one load may make while pages hold nothing usable, so it always ends. */
export const GBP_PHOTO_MAX_REQUESTS = 5;

/**
 * Reads the Media Library from `startToken` and keeps only images the profile can
 * take (isOwnMediaUrl). A page can hold none of those, or come back short with more
 * behind it (the API filters after reading), so this follows next_token until it has
 * at least one image or the library ends, at most GBP_PHOTO_MAX_REQUESTS times.
 */
export async function loadGbpPhotoPage(
  fetchPage: (nextToken: string | null) => Promise<{ medias: MediaItem[]; nextToken: string | null }>,
  customerId: string,
  startToken: string | null = null,
): Promise<GbpPhotoPage> {
  const items: MediaItem[] = [];
  let token = startToken;
  for (let i = 0; i < GBP_PHOTO_MAX_REQUESTS; i++) {
    const page = await fetchPage(token);
    for (const m of page.medias) {
      if (isImageMediaItem(m) && isOwnMediaUrl(getMediaThumbnailUrl(m), customerId)) items.push(m);
    }
    token = page.nextToken;
    if (!token || items.length > 0) break;
  }
  return { items, nextToken: token };
}

/** One approved change, ready to send: a profile edit, or a photo. */
export type ApplyItem =
  | {
      key: string;
      field: GbpEditField;
      value: GbpEditValue;
      /** The lost buyer questions this fix targets, for the next check (SGL-328 #2). */
      questions?: string[];
    }
  | { key: string; field: "photo"; photo: { sourceUrl: string; category: GbpPhotoCategory } };

/** The rows that can be applied right now, keyed for the checkboxes. */
export function applicableItems(s: ProposalSections): ApplyItem[] {
  const items: ApplyItem[] = [];
  for (const r of s.services) {
    if (!isSettled(r.outcome)) {
      items.push({
        key: r.key,
        field: "services",
        value: [r.group.service],
        questions: r.group.questions.map((q) => q.question),
      });
    }
  }
  for (const r of s.ask) {
    // Typical hours are a reference, not the owner's hours: never sent as they are.
    if (r.appliable && r.value && r.spec.field && r.source !== "typical" && !isSettled(r.outcome)) {
      items.push({ key: r.key, field: r.spec.field, value: editValue(r.value) });
    }
  }
  for (const r of s.attributes) {
    if (r.value !== null && !isSettled(r.outcome)) {
      items.push({ key: r.key, field: "attributes", value: { attribute: r.option.id, value: r.value } });
    }
  }
  if (s.photo?.value?.category && !isSettled(s.photo.outcome)) {
    items.push({
      key: s.photo.key,
      field: "photo",
      photo: { sourceUrl: s.photo.value.sourceUrl, category: s.photo.value.category },
    });
  }
  if (s.primary?.value && !isSettled(s.primary.outcome)) {
    items.push({ key: s.primary.key, field: "primary_category", value: s.primary.value.name });
  }
  return items;
}

/** The refusals the main-category card can meet, in the owner's words. */
const PRIMARY_REFUSALS: Record<string, string> = {
  already_set: "That's already your main category on Google.",
  in_additional_categories:
    "That's already one of your additional categories. Choose a different one, or remove it from your additional categories on Google first.",
};

/** Said when Google took a write we could not read back and the server sent no words. */
export const UNVERIFIED_MESSAGE =
  "Sent to Google, but I couldn't confirm it yet. I'll check again on the next visibility check.";

/** A finished edit, from the API result. */
export function outcomeFromResult(r: GbpEditResult, at: Date = new Date()): EditOutcome {
  const status =
    r.status === "live" || r.status === "pending_review" || r.status === "applied_unverified" || r.status === "no_answer"
      ? r.status
      : "failed";
  const refusal = r.field === "primary_category" && r.reason ? PRIMARY_REFUSALS[r.reason] : undefined;
  const gone = Array.isArray(r.services_no_longer_on_google)
    ? r.services_no_longer_on_google.filter((x): x is string => typeof x === "string" && !!x.trim())
    : [];
  const one = gone.length === 1;
  return {
    status,
    old: r.old ?? null,
    now: r.now_on_google ?? r.new ?? null,
    ...(status !== "failed" && r.new != null ? { check: { field: r.field, value: r.new } } : {}),
    message:
      status === "failed"
        ? refusal || r.message || "Google didn't accept this change."
        : status === "applied_unverified"
          ? r.message || UNVERIFIED_MESSAGE
          : status === "no_answer"
            ? r.message || NO_ANSWER_MESSAGE
            : undefined,
    ...(gone.length
      ? {
          warning:
            `Google no longer shows ${one ? "this service" : "these services"} on your profile: ${gone.join(", ")}. ` +
            `Add ${one ? "it" : "them"} again if you still offer ${one ? "it" : "them"}.`,
        }
      : {}),
    at: at.toISOString(),
  };
}

/** Said when a request went out and no usable answer came back. */
export const NO_ANSWER_MESSAGE = "I couldn't get an answer. Check again in a moment — it may already be on Google.";
/** A photo sent twice is two photos on Google, so the retry warning is explicit. */
export const NO_ANSWER_PHOTO_MESSAGE =
  "I couldn't get an answer. Check your profile on Google in a moment — the photo may already be there, and sending it again would add it twice.";

/**
 * The outcome of a write that threw instead of returning a result.
 *
 * Only a 4xx from our own API is known to have stopped before Google (see
 * gbpService.GbpWriteError): that is "not changed". Anything else — a network
 * error or timeout after the request left, a 5xx, an unreadable body — may have
 * reached Google, so it is never reported as "nothing was changed".
 */
export function outcomeFromRequestError(err: unknown, field: string, at: Date = new Date()): EditOutcome {
  const refused = isRecord(err) && err.refusedBeforeGoogle === true;
  if (refused) {
    return { status: "failed", message: "Sagelyn couldn't send this change. Nothing was changed.", at: at.toISOString() };
  }
  return {
    status: "no_answer",
    message: field === "photo" ? NO_ANSWER_PHOTO_MESSAGE : NO_ANSWER_MESSAGE,
    at: at.toISOString(),
  };
}

/** A finished photo upload, from the API result. */
export function outcomeFromPhotoResult(r: GbpPhotoResult, at: Date = new Date()): EditOutcome {
  if (r.status === "submitted") {
    return {
      status: "submitted",
      now: r.new?.source_url ?? null,
      at: at.toISOString(),
      ...(r.media_name ? { check: { field: "photo" as const, mediaName: r.media_name } } : {}),
    };
  }
  if (r.status === "no_answer") {
    return { status: "no_answer", message: r.message || NO_ANSWER_PHOTO_MESSAGE, at: at.toISOString() };
  }
  return { status: "failed", message: r.message || "Google didn't accept this photo.", at: at.toISOString() };
}

/** GET /gbp/attributes, cut down for the card. Anything but "ok" offers nothing. */
export function attributesFromResponse(resp: GbpAttributesResponse | null | undefined): GbpAttributesState {
  if (!resp || resp.status !== "ok" || !Array.isArray(resp.attributes)) return { status: "failed" };
  const items = resp.attributes.filter(
    (a): a is GbpAttributeOption =>
      isRecord(a) &&
      typeof a.id === "string" &&
      !!a.id &&
      (a.value_type === "BOOL" || a.value_type === "ENUM") &&
      Array.isArray(a.options),
  );
  const n = typeof resp.not_editable_count === "number" && resp.not_editable_count > 0 ? resp.not_editable_count : 0;
  return { status: "ok", items, notEditableCount: n };
}

/** The profile audit response, cut down for the card: the first location we
 *  could read. A profile we could not read keeps its reason. */
export function auditFromResponse(resp: GbpAuditResponse | null | undefined): GbpAuditState {
  if (!resp || !resp.connected) return { status: "not_connected" };
  const locations = Array.isArray(resp.locations) ? resp.locations : [];
  const read = locations.find((l): l is GbpAuditLocation => "business_info" in l);
  if (read && read.status !== "needs_reauth") {
    const info = read.business_info;
    const cat = info && info.status === "ok" ? info.primary_category : null;
    return {
      status: "ok",
      locationId: read.location_id,
      gaps: Array.isArray(read.gaps) ? read.gaps : [],
      primaryCategory: cat && cat.id && cat.display_name ? { name: cat.id, display_name: cat.display_name } : null,
    };
  }
  if (locations.some((l) => l.status === "needs_reauth")) return { status: "needs_reauth" };
  if (locations.length > 0 && locations.every((l) => l.status === "not_connected")) {
    return { status: "not_connected" };
  }
  return { status: "failed" };
}

/** The gap codes worth asking chat-service to suggest values for. */
export function suggestableGaps(audit: GbpAuditState | null | undefined): string[] {
  if (!audit || audit.status !== "ok") return [];
  // Hours and opening date have no source to suggest from: the owner sets them.
  return audit.gaps
    .map((g) => g.code)
    .filter((c) => c in ASK_GAPS && ASK_GAPS[c].kind !== "hours" && ASK_GAPS[c].kind !== "date");
}

/** The first agent of each hand-off role the owner already has. */
export function pickTeam(
  agents: Array<{ id: string; name: string; agentType?: string }> | undefined,
): GbpProposalTeam {
  const find = (match: (t: string) => boolean): GbpTeamMember | null => {
    const a = (agents ?? []).find((x) => match((x.agentType ?? "").trim().toLowerCase()));
    return a ? { id: a.id, name: a.name } : null;
  };
  return {
    receptionist: find((t) => t.startsWith("communication_agent")),
    socialMediaManager: find((t) => t === "content_creator"),
  };
}

/* ── storing it ────────────────────────────────────────────────────────── */

/** What gets stored on the thread: the card's state, not its transient bits. */
export function storableProposal(p: GbpProposalAttachment): Record<string, unknown> {
  const applied: Record<string, EditOutcome> = {};
  for (const [k, o] of Object.entries(p.applied ?? {})) {
    // An edit still in flight is not a result; the next save will carry it.
    if (o.status !== "applying") applied[k] = o;
  }
  return {
    failed: p.failed,
    findings: p.findings,
    audit: p.audit ?? null,
    team: p.team ?? null,
    suggestions: p.suggestions ?? {},
    answers: p.answers ?? {},
    applied,
    googleAutoOff: !!p.googleAutoOff,
    ...(p.googleUpdatedNow ? { googleUpdatedNow: p.googleUpdatedNow } : {}),
    ...(p.googleBackOn ? { googleBackOn: true } : {}),
    dismissed: !!p.dismissed,
    attributes: p.attributes ?? null,
    attributeAnswers: p.attributeAnswers ?? {},
    primaryAnswer: p.primaryAnswer ?? null,
    photo: p.photo ?? null,
    ...(p.plan
      ? {
          plan: p.plan,
          planAnswers: p.planAnswers ?? {},
          skipped: p.skipped ?? {},
          doneOnGoogle: p.doneOnGoogle ?? {},
          revisedInChat: p.revisedInChat ?? {},
        }
      : {}),
    ...(p.planError ? { planError: p.planError } : {}),
    // The agent's reply above a typed request already gives the plan's summary;
    // kept so a card reopened from History doesn't repeat it.
    ...(p.askedInChat ? { askedInChat: true } : {}),
  };
}

/** A stored v2 plan, checked enough to render: a version-2 object with a row list.
 *  Each row is read defensively where it is used (utils/gbpPlan.ts). */
function storedPlan(v: unknown): GbpPlan | null {
  return isRecord(v) && v.version === 2 && typeof v.status === "string" && Array.isArray(v.rows)
    ? (v as unknown as GbpPlan)
    : null;
}

const flags = (v: unknown): Record<string, boolean> =>
  isRecord(v) ? Object.fromEntries(Object.entries(v).filter(([, x]) => x === true)) as Record<string, boolean> : {};

const OUTCOME_STATUSES = new Set(["live", "pending_review", "applied_unverified", "submitted", "no_answer", "not_on_google", "failed"]);
const SOURCES = new Set(["brand_details", "drafted", "google_categories"]);

/** A stored card back into an attachment (History reload). Defensive: the
 *  row is ours, but it crossed DynamoDB and an older card may lack fields. */
export function proposalFromStored(
  raw: unknown,
  threadId: string | null,
  sk: string | null,
): GbpProposalAttachment | null {
  if (!isRecord(raw)) return null;
  const answers: Record<string, FixValue> = {};
  if (isRecord(raw.answers)) {
    for (const [code, v] of Object.entries(raw.answers)) {
      const spec = ASK_GAPS[code];
      const value = spec ? asFixValue(spec.kind, v) : null;
      if (value) answers[code] = value;
    }
  }
  const suggestions: Record<string, StoredSuggestion> = {};
  if (isRecord(raw.suggestions)) {
    for (const [code, s] of Object.entries(raw.suggestions)) {
      if (isRecord(s) && typeof s.source === "string" && SOURCES.has(s.source)) {
        suggestions[code] = { value: s.value, source: s.source as GbpSuggestionSource };
      }
    }
  }
  const applied: Record<string, EditOutcome> = {};
  if (isRecord(raw.applied)) {
    for (const [key, o] of Object.entries(raw.applied)) {
      if (isRecord(o) && typeof o.status === "string" && OUTCOME_STATUSES.has(o.status)) {
        applied[key] = o as unknown as EditOutcome;
      }
    }
  }
  const member = (v: unknown): GbpTeamMember | null =>
    isRecord(v) && typeof v.id === "string" && typeof v.name === "string" ? { id: v.id, name: v.name } : null;
  const team = isRecord(raw.team)
    ? { receptionist: member(raw.team.receptionist), socialMediaManager: member(raw.team.socialMediaManager) }
    : undefined;
  const audit = isRecord(raw.audit) && typeof raw.audit.status === "string" ? (raw.audit as GbpAuditState) : null;
  const attributes: GbpAttributesState | null =
    isRecord(raw.attributes) && raw.attributes.status === "ok" && Array.isArray(raw.attributes.items)
      ? attributesFromResponse({
          status: "ok",
          attributes: raw.attributes.items as GbpAttributeOption[],
          not_editable_count: typeof raw.attributes.notEditableCount === "number" ? raw.attributes.notEditableCount : 0,
        })
      : isRecord(raw.attributes) && raw.attributes.status === "failed"
        ? { status: "failed" }
        : null;
  const attributeAnswers: Record<string, boolean | string> = {};
  if (isRecord(raw.attributeAnswers)) {
    for (const [id, v] of Object.entries(raw.attributeAnswers)) {
      if (typeof v === "boolean" || typeof v === "string") attributeAnswers[id] = v;
    }
  }
  return {
    loading: false,
    failed: raw.failed === true,
    findings: isRecord(raw.findings) ? (raw.findings as unknown as ProfileFindings) : null,
    audit,
    team,
    suggestions,
    answers,
    applied,
    googleAutoOff: raw.googleAutoOff === true,
    ...(Array.isArray(raw.googleUpdatedNow)
      ? { googleUpdatedNow: raw.googleUpdatedNow.filter((x): x is string => typeof x === "string") }
      : {}),
    ...(raw.googleBackOn === true ? { googleBackOn: true } : {}),
    dismissed: raw.dismissed === true,
    attributes,
    attributeAnswers,
    primaryAnswer: isCategory(raw.primaryAnswer) ? raw.primaryAnswer : null,
    photo: isPhotoPick(raw.photo) ? raw.photo : null,
    ...(storedPlan(raw.plan)
      ? {
          plan: storedPlan(raw.plan),
          planAnswers: isRecord(raw.planAnswers) ? (raw.planAnswers as Record<string, GbpEditValue>) : {},
          skipped: flags(raw.skipped),
          doneOnGoogle: flags(raw.doneOnGoogle),
          revisedInChat: flags(raw.revisedInChat),
        }
      : {}),
    ...(typeof raw.planError === "string" ? { planError: raw.planError } : {}),
    ...(raw.askedInChat === true ? { askedInChat: true } : {}),
    threadId,
    cardMessageSk: sk,
  };
}

/* ── words ─────────────────────────────────────────────────────────────── */

/** What the agent says when the last check has nothing to propose. */
export function noProposalMessage(block: ProfileFindings | null, failed: boolean): string {
  if (failed) return "I couldn't load your last check just now. Please try again in a moment.";
  if (!isRecord(block)) {
    return "I haven't compared your Google Business Profile yet. Run a check in Analytics → AI Visibility and I'll look at it.";
  }
  switch (block.status) {
    case "ok": {
      const n = typeof block.questions_checked === "number" && block.questions_checked > 0
        ? plural(block.questions_checked, "local search", "local searches")
        : "the local searches";
      return `I checked your Google Business Profile against ${n} you lost and didn't find a missing service.`;
    }
    case "no_local_lost_questions":
      return "You didn't lose any local searches in your last check, so there's nothing on your profile to fix right now.";
    case "not_connected":
      return "Your Google Business Profile isn't connected. Connect it in Connections and I'll check it on your next run.";
    case "needs_reauth":
      return "Your Google connection has expired. Reconnect it in Connections and I'll check your profile on your next run.";
    default:
      return "I couldn't compare your Google Business Profile in the last check. The next check will try again.";
  }
}

/** One sentence when the live profile could not be read, or null when it was. */
export function auditProblem(audit: GbpAuditState | null | undefined): string | null {
  if (!audit || audit.status === "ok") return null;
  if (audit.status === "not_connected") return "Your Google Business Profile isn't connected, so I couldn't read it.";
  if (audit.status === "needs_reauth") return "Your Google connection has expired. Reconnect it in Connections so I can read your profile.";
  return "I couldn't read your Google Business Profile just now.";
}

/**
 * The one action that fixes a profile the card could not read: connecting it. Null when
 * the profile was read, or when it failed for a reason connecting would not fix (a
 * flaky read). The card can learn "not connected" from either the live audit or the last
 * mention check, so both are read; a successful audit wins, since it did read the profile.
 */
export function gbpConnectLabel(p: {
  audit?: GbpAuditState | null;
  findings?: ProfileFindings | null;
}): string | null {
  const audit = p.audit?.status;
  if (audit === "ok") return null;
  const findings = isRecord(p.findings) ? p.findings.status : undefined;
  if (audit === "not_connected" || findings === "not_connected") return "Connect Google Business Profile";
  if (audit === "needs_reauth" || findings === "needs_reauth") return "Reconnect Google";
  return null;
}

function outcomeText(o: EditOutcome | null): string {
  if (!o) return "";
  if (o.status === "live") return " Applied on Google: live.";
  if (o.status === "pending_review") return " Sent to Google: waiting for Google's review, not live yet.";
  if (o.status === "applied_unverified") {
    return " Sent to Google but not confirmed yet; the next visibility check looks again. Not known to be live.";
  }
  if (o.status === "submitted") return " Photo sent to Google; not yet confirmed on the profile.";
  if (o.status === "no_answer") {
    return " Sent, but no answer came back, so it is not known whether Google has it; the owner should check before trying again.";
  }
  if (o.status === "not_on_google") {
    return " Sent earlier, but a later check found Google doesn't show it and nothing is pending (changed back or not accepted).";
  }
  if (o.status === "failed") return ` Google didn't accept it: ${o.message ?? "no reason given"}.`;
  return " Being sent to Google.";
}

/**
 * The card as text, for the model (stored as the turn's `summary` and used by
 * assistantStandInText): what the owner is looking at, named, and exactly what
 * was and wasn't changed on Google, so the agent never claims more than happened.
 */
export function describeGbpProposal(p: GbpProposalAttachment): string {
  if (p.loading) return "[Reading the owner's Google Business Profile check.]";
  const s = readSections(p);
  if (!hasAnything(s)) {
    const parts = [noProposalMessage(p.findings, p.failed), auditProblem(p.audit)].filter(Boolean);
    return `[${parts.join(" ")}]`;
  }
  const lines: string[] = ["[Showed the owner their Google Business Profile fixes."];
  if (s.services.length > 0) {
    if (s.lost > 0) lines.push(`AI recommends someone else for ${plural(s.lost, "local search", "local searches")}.`);
    for (const r of s.services) {
      lines.push(
        `Add the service "${r.group.service}" (affects ${plural(r.group.questions.length, "local search", "local searches")}).${outcomeText(r.outcome)}`,
      );
    }
  }
  for (const r of s.ask) {
    const proposed =
      r.source === "typical"
        ? "not set; typical hours shown as a starting point, waiting for the owner to set theirs"
        : r.value
          ? `proposed "${formatValue(r.value)}" (${SOURCE_LABELS[r.source ?? "owner"].toLowerCase()})`
          : "not set, waiting for the owner";
    const manual = r.appliable ? "" : " Sagelyn can't set this yet; the owner sets it on Google.";
    lines.push(`${r.spec.title}: ${proposed}.${manual}${outcomeText(r.outcome)}`);
  }
  for (const r of s.attributes) {
    const name = r.option.display_name || r.option.id;
    const chosen = r.value === null ? "no value chosen yet" : `the owner chose "${attributeValueLabel(r.option, r.value)}"`;
    lines.push(`Attribute "${name}" is not set on Google: ${chosen}.${outcomeText(r.outcome)}`);
  }
  if (s.attributesNotEditable > 0) {
    lines.push(`${plural(s.attributesNotEditable, "more attribute", "more attributes")} can only be changed on Google by the owner.`);
  }
  if (s.photo) {
    const cat = s.photo.value?.category ? photoCategoryLabel(s.photo.value.category).toLowerCase() : null;
    const picked = s.photo.value
      ? `the owner picked a Media Library photo${cat ? ` (${cat})` : ", no category chosen yet"}`
      : "no photo picked yet";
    lines.push(`The profile has no photos: ${picked}.${outcomeText(s.photo.outcome)}`);
  }
  if (s.primary) {
    const to = s.primary.value ? `the owner picked "${s.primary.value.display_name}"` : "the owner has not picked a change";
    lines.push(
      `Main category is "${s.primary.current.display_name}", offered as optional, not as a problem: ${to}.${outcomeText(s.primary.outcome)}`,
    );
    if (s.primary.outcome?.warning) lines.push(s.primary.outcome.warning);
  }
  if (s.handOffs.length > 0) {
    const handOffs = s.handOffs
      .map((h) => `${h.spec.title} (${h.detail || h.code}) is for ${h.member ? h.member.name : `a ${h.spec.roleLabel} the owner doesn't have yet`}`)
      .join("; ");
    lines.push(`For the rest of the team: ${handOffs}. Nothing has been handed over; the owner can ask them.`);
  }
  if (s.notes.length > 0) {
    lines.push(`Also noted: ${s.notes.map((n) => n.detail || n.title).join("; ")}.`);
  }
  const problem = auditProblem(p.audit);
  if (problem) lines.push(problem);
  lines.push(
    p.googleAutoOff
      ? "The owner confirmed Google's automatic profile updates are off."
      : "Asked the owner to turn off Google's automatic profile updates first; not confirmed yet.",
  );
  if (p.dismissed) lines.push("The owner chose 'Not now'.");
  const sent = Object.values(p.applied ?? {}).filter(isSettled).length;
  const unanswered = Object.values(p.applied ?? {}).some((o) => o.status === "no_answer");
  lines.push(
    sent === 0 && unanswered
      ? "Nothing is confirmed on Google; the rows marked above got no answer and may or may not be there.]"
      : sent === 0
      ? "Nothing has been changed on Google yet.]"
      : `Only the rows marked above were sent to Google; nothing else was changed.]`,
  );
  return lines.join(" ");
}

/** An attribute value in the owner's words: Yes/No, or Google's own label. */
export function attributeValueLabel(option: GbpAttributeOption, value: boolean | string | null | undefined): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return option.options.find((o) => o.value === value)?.display_name || value;
}

export const photoCategoryLabel = (c: GbpPhotoCategory): string =>
  GBP_PHOTO_CATEGORIES.find((x) => x.value === c)?.label ?? c;
